import {
  StructuredText as IStructuredText,
  isParagraph,
} from 'datocms-structured-text-utils';
import React, { FC, ReactElement, ReactNode, useEffect, useRef } from 'react';
import { StructuredText, renderRule } from 'react-datocms';
import '../../sass/styles.scss';

import { ImageProps } from '../../../..//growth-platform-brand-system-v2/src/components/Image/types';
import { ButtonV2Props } from '../../../../growth-platform-brand-system-v2/src/components/ButtonV2';
import { Image } from '../../../../growth-platform-brand-system-v2/src/components/Image';
import { textSerifTitle2 } from '../../../../growth-platform-brand-system-v2/src/styles/typography';
import { scrollAnimate } from '../../../../growth-platform-brand-system-v2/src/utils/scrollAnimate';
import { classNames } from '../../../../growth-platform-brand-system-v2/src/utils/style';
import { applyBgColor } from './logic';

export interface AskADemoProps {
  title?: IStructuredText;
  ctas?: Array<ReactElement<ButtonV2Props>>;
  backgroundColor?: 'blue' | 'white';
  isDark?: boolean;
  isEncapsulated?: boolean;
  image?: ImageProps;
  className?: string;
  disabledAnimation?: boolean;
  disableImageOnMobile?: boolean;
  videoComponent?: ReactNode;
}

export const AskADemo: FC<AskADemoProps> = ({
  title,
  ctas,
  backgroundColor = 'blue',
  isDark = false,
  isEncapsulated = false,
  image,
  className,
  disabledAnimation = false,
  disableImageOnMobile = false,
  videoComponent,
}) => {
  const refSection = useRef(null);

  const textOnly = !image && !videoComponent;

  useEffect(() => scrollAnimate(refSection.current), [refSection]);

  return (
    <section className={classNames('ask-a-demo', className)}>
      <div
        ref={refSection}
        className={classNames(
          !disabledAnimation && 'slideInUp-30 wait',
          isEncapsulated && 'container rounded-18',
          applyBgColor(backgroundColor, isDark),
        )}
      >
        <div
          className={classNames(
            !isEncapsulated && 'container',
            textOnly
              ? 'py-40 sm:py-64 md:py-88'
              : 'md:flex md:flex-row-reverse items-stretch ',
          )}
        >
          <div
            className={classNames(
              !textOnly &&
                (isEncapsulated
                  ? 'md:w-1/2 xl:w-7/12  md:pr-40 lg:pr-104'
                  : 'md:w-1/2 xl:w-8/12 lg:pr-104'),
              textOnly
                ? 'flex flex-col lg:flex-row items-center justify-center'
                : 'py-24 sm:py-40 md:py-104',
              videoComponent ? 'flex flex-col justify-center' : '',
              'px-24 md:px-12',
            )}
          >
            {title?.value !== null && (
              <StructuredText
                data={title}
                customNodeRules={[
                  renderRule(isParagraph, ({ children, key }) => (
                    <p
                      key={key}
                      className={classNames(
                        'title',
                        textSerifTitle2,
                        `font-medium lg:pr-56 text-center ${
                          isDark ? 'text-neutral-1' : ' text-neutral-301'
                        }  ${
                          textOnly
                            ? 'md:w-1/2 lg:w-3/8 xl:w-5/12 pb-24 sm:pb-36 lg:pb-0 lg:text-left'
                            : 'pb-24 md:text-left'
                        }`,
                      )}
                    >
                      {children}
                    </p>
                  )),
                ]}
              />
            )}
            {ctas && (
              <div
                className={`lg:flex ${
                  textOnly ? 'lg:w-3/8 xl:w-3/12 flex items-center' : '-mb-16'
                }`}
              >
                {ctas.reduce((acc: any[], cta: any, key: number) => {
                  if (textOnly && key !== 0) {
                    return acc;
                  }

                  acc.push(cta);
                  return acc;
                }, [])}
              </div>
            )}
          </div>
          {(image || videoComponent) && (
            <div
              className={classNames(
                isEncapsulated
                  ? 'xl:w-5/12 sm:py-24 md:py-40 px-24 md:pl-40 md:pr-12 lg:pr-64'
                  : 'xl:w-4/12 md:py-40 md:px-12 lg:mr-64',
                'pb-24',
                disableImageOnMobile
                  ? 'hidden md:flex md:justify-center md:items-center md:w-1/2 md:lg:w-3/8'
                  : 'flex justify-center items-center w-full md:w-1/2 lg:w-3/8',
              )}
            >
              {image && (
                <div
                  className={
                    'relative overflow-hidden rounded-12 image-size w-full'
                  }
                >
                  <Image
                    alt={image?.alt || ''}
                    src={image?.url}
                    width={image?.width}
                    height={image?.height}
                    className="w-full h-full rounded-12 object-cover"
                    pixelDensity="default"
                    sizes={[
                      '(max-width: 350px) 256px',
                      '(max-width: 480px) 381px',
                      '(max-width: 768px) 534px',
                      '(max-width: 1024px) 316px',
                      '(max-width: 1280px) 396px',
                      '415px',
                    ]}
                  />
                  <div
                    className="absolute inset-0"
                    style={{
                      background:
                        'linear-gradient(rgba(0, 0, 0, 0.13), rgba(0, 0, 0, 0.13))',
                    }}
                  />
                </div>
              )}

              {videoComponent && videoComponent}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
